import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { Form, Col, Select, Button, Row, Empty } from "antd";
import {
  CardElement,
  HeaderLeftItemsWrapper,
  HMGSelect,
  ProjectPMSSelect,
  ProjectSelect,
} from "../../components/Common";
import {
  REPORT_TYPES,
  convertReportFieldsToText,
  getAvailableDocTypes,
} from "../../utils/report";
import { PMS_ALL_VALUE } from "../../utils/pms.utils";
import {
  useGetProjectHotelsSubHeaderData,
  useGetProjectDistinctReportNamesByReportType,
  useGetProjectHotels,
  useGetProjectPMSDistinctReportNamesByReportType,
  useGetProjectReportCategories,
  useGetProjectReports,
  useGetProjectSmartScan,
} from "../../hooks/project.hooks";
import ReportBulkEditTable from "../../components/Report/ReportBulkEditTable";
import { useGetLMDDocumentTypes } from "../../hooks";

const { Option } = Select;

const BulkReportUpdateHome = ({ isAdminUser }) => {
  const history = useHistory();
  const [hmg, setHmg] = useState();
  const [project, setProject] = useState();
  const [pms, setPMS] = useState();
  const [frequency, setFrequency] = useState();
  const [reportName, setReportName] = useState();
  const [selecteIds, setSelectedIds] = useState([]);

  const setHmgRef = useCallback((list) => {
    if (list?.length === 1) setHmg(list[0]._id);
  }, []);
  const setProjectRef = useCallback((list) => {
    if (list?.length === 1) setProject(list[0]._id);
  }, []);
  const setPMSRef = useCallback((list) => {
    if (list?.length === 1) setPMS(list[0]._id);
  }, []);

  useEffect(() => {
    setFrequency();
  }, [pms]);
  useEffect(() => {
    setReportName();
  }, [frequency]);

  const { data: documentTypes } = useGetLMDDocumentTypes();

  const { data: pmsReportNames, isLoading: isPMSReportNamesLoading } =
    useGetProjectPMSDistinctReportNamesByReportType(project, pms, frequency, {
      enabled: !!(project && pms && pms !== "all" && frequency),
    });

  const { data: allReportNames, isLoading: isReportNamesLoading } =
    useGetProjectDistinctReportNamesByReportType(
      project,
      {
        enabled: !!(project && pms === "all" && frequency),
      },
      { shedule_type: frequency },
    );

  const reportNameList = useMemo(() => {
    if (pms && pms !== "all" && pmsReportNames) {
      return pmsReportNames;
    }
    if (pms === "all" && allReportNames) {
      return allReportNames;
    }
    return [];
  }, [pmsReportNames, allReportNames]);

  useEffect(() => {
    if (Array.isArray(reportNameList) && reportNameList.length === 1) {
      setReportName(reportNameList[0]);
    }
  }, [reportNameList]);

  const { data: projectSmartScanData } = useGetProjectSmartScan(project, {
    enabled: !!project,
  });

  const { data: projectSubHeaderData } =
    useGetProjectHotelsSubHeaderData(project);

  const { data: projectReportCategories } = useGetProjectReportCategories(
    project,
    {
      enabled: !!project,
    },
  );

  const {
    data: reportData,
    isLoading: isLoadingReportList,
    refetch: refetchReports,
  } = useGetProjectReports(
    project,
    { ...(reportName ? { ReportName: reportName } : {}) },
    {
      enabled: false,
      cacheTime: 0,
    },
  );

  const { data: hotels } = useGetProjectHotels(project, {
    enabled: !!project,
  });

  const hotelReports = useMemo(() => {
    if (hotels && reportData) {
      const hotelMap = new Map(hotels.map((hotel) => [hotel._id, hotel]));
      // Only filter by PMS when a specific one is selected ("All" -> no filter).
      const filterByPms = pms && pms !== PMS_ALL_VALUE;
      const filteresReports = reportData.filter((report) => {
        if (
          !hotelMap.has(report.hotel_id) ||
          report.shedule_type !== frequency
        ) {
          return false;
        }
        if (filterByPms) {
          const hotel = hotelMap.get(report.hotel_id);
          return (
            Array.isArray(hotel?.pms) &&
            hotel.pms.some((entry) => entry?.pms_id?._id === pms)
          );
        }
        return true;
      });
      return filteresReports;
    }
    return [];
  }, [reportData, pms, hotels, frequency]);

  const availableDocTypes = useMemo(
    () =>
      getAvailableDocTypes({
        hotelData: hotels ?? [],
        selectedHotels: reportData
          ? reportData
              .filter((report) => selecteIds.includes(report._id))
              .map((report) => report.hotel_id)
          : [],
        documentTypes: documentTypes ?? [],
      }),
    [hotels, reportData, documentTypes, selecteIds],
  );

  const tableData = useMemo(() => {
    if (hotels) {
      const hotelMap = new Map(hotels.map((hotel) => [hotel._id, hotel]));
      const projectHotelsSubHeaderMap = new Map(
        (projectSubHeaderData ?? []).map((hotel) => [hotel._id, hotel]),
      );
      return hotelReports.map((filteredReport) => {
        const selectedHotel = hotelMap.get(filteredReport.hotel_id);
        const hotelSubHeaderData = projectHotelsSubHeaderMap.get(
          filteredReport.hotel_id,
        );
        return {
          ...convertReportFieldsToText(filteredReport, {
            projectReportCategories,
            projectSmartScanData,
            // Display the saved document types from the full list; availableDocTypes
            // depends on the selected rows and is empty until a row is checked.
            documentTypes: documentTypes ?? [],
            hotelSubHeaderData,
          }),
          hotel_id: selectedHotel.hotel_code,
        };
      });
    }
    return [];
  }, [
    hotelReports,
    hotels,
    projectSubHeaderData,
    projectReportCategories,
    projectSmartScanData,
    documentTypes,
  ]);

  return (
    <CardElement title="Advanced Report Management - Bulk Edit">
      <Col xxl={20} xl={24} lg={24} md={24} sm={24}>
        <Form layout="vertical">
          <HeaderLeftItemsWrapper>
            <Col xxl={5} xl={5} lg={5} md={10} sm={24} xs={24}>
              <Form.Item label="Hotel Management Group">
                <HMGSelect
                  value={hmg}
                  onChange={setHmg}
                  autoDisable
                  setHmgListRef={setHmgRef}
                />
              </Form.Item>
            </Col>
            <Col xxl={5} xl={4} lg={5} md={10} sm={24} xs={24}>
              <Form.Item label="Project">
                <ProjectSelect
                  hmgId={hmg}
                  value={project}
                  onChange={setProject}
                  disabled={!hmg}
                  setProjectListRef={setProjectRef}
                />
              </Form.Item>
            </Col>
            <Col xxl={3} xl={3} lg={3} md={4} sm={24} xs={24}>
              <Form.Item label="PMS">
                <ProjectPMSSelect
                  projectId={project}
                  value={pms}
                  onChange={setPMS}
                  disabled={!project}
                  setProjectPMSListRef={setPMSRef}
                />
              </Form.Item>
            </Col>
            <Col xxl={5} xl={4} lg={5} md={8} sm={24} xs={24}>
              <Form.Item label="Report Frequency">
                <Select
                  value={frequency}
                  onChange={setFrequency}
                  placeholder="Report Frequency"
                  disabled={!pms}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    return (
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {REPORT_TYPES.map((rtype) => {
                    return (
                      <Option key={rtype.ReportType} value={rtype.ReportType}>
                        {rtype.ReportType}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xxl={5} xl={5} lg={5} md={8} sm={24} xs={24}>
              <Form.Item label="Report Name">
                <Select
                  value={reportName}
                  onChange={setReportName}
                  placeholder="Report Name"
                  disabled={!frequency}
                  loading={isReportNamesLoading || isPMSReportNamesLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    return (
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {reportNameList
                    ? reportNameList.map((distinctReportName) => (
                        <Option
                          value={distinctReportName}
                          key={distinctReportName}
                        >
                          {distinctReportName}
                        </Option>
                      ))
                    : []}
                </Select>
              </Form.Item>
            </Col>
            <Col xxl={1} xl={1} lg={1} md={2} sm={4} xs={4}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  disabled={!reportName}
                  onClick={refetchReports}
                  loading={isLoadingReportList}
                >
                  Go
                </Button>
              </Form.Item>
            </Col>
          </HeaderLeftItemsWrapper>
        </Form>
      </Col>
      {tableData?.length > 0 ? (
        <>
          <Row justify="end">
            <Button
              type="link"
              onClick={() =>
                history.push("/report-management/bulk/edit", {
                  reports: hotelReports.filter((report) =>
                    selecteIds.includes(report._id),
                  ),
                  project,
                  frequency,
                  reportName,
                  documentTypes: availableDocTypes,
                })
              }
              disabled={selecteIds.length === 0}
            >
              Update and Apply to All
            </Button>
          </Row>
          <ReportBulkEditTable
            frequency={frequency}
            tableData={tableData}
            setSelectedRowKeys={setSelectedIds}
            selectedRowKeys={selecteIds}
          />
        </>
      ) : (
        <Empty
          description={
            <p>
              Please select the <strong>Filters</strong> to display Reports
            </p>
          }
        />
      )}
    </CardElement>
  );
};

BulkReportUpdateHome.propTypes = {};

export default BulkReportUpdateHome;
